import { AddSourcesContent } from "@mirage/mosaics/Chat/components/compose-sources/AddSourcesContent";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import { useMountEffect } from "@react-hookz/web";
import { isEqual } from "lodash";
import { useCallback, useEffect } from "react";
import { useSourcesCache } from "./SourcesCacheContext";

import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { useWorkflowAgentAddedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import { mergeAndDeduplicateSources } from "@mirage/service-conversation/utils/mergeAndDeduplicateSources";
import i18n from "@mirage/translations";

export const SourceSelect = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (sourceIds: string[]) => void;
}) => {
  const { sourcesContentCache, updateSources, getSourcesByIds } =
    useSourcesCache();
  const {
    workflowAgentAddedSources,
    setWorkflowAgentAddedSources,
    workflowAgentAddedSourcesUUIDs,
    setWorkflowAgentAddedSourcesUUIDs,
  } = useWorkflowAgentAddedSources();

  // Hydrate sources from UUIDs when component mounts or value changes
  useMountEffect(() => {
    const hydrateSourcesFromIds = async () => {
      // Use getSourceById to hydrate sources from UUIDs
      const hydratedSources = await getSourcesByIds(value);
      if (hydratedSources.length > 0) {
        const sources = mergeAndDeduplicateSources(
          hydratedSources,
          workflowAgentAddedSources,
        );
        setWorkflowAgentAddedSources(sources);
        setWorkflowAgentAddedSourcesUUIDs(
          sources.map(getSourceUUID).filter(isDefined),
        );
      }
    };

    hydrateSourcesFromIds();
  });

  // Update the context when sources change
  useEffect(() => {
    if (workflowAgentAddedSources.length > 0) {
      updateSources(workflowAgentAddedSources);
    }
  }, [workflowAgentAddedSources, updateSources]);

  const removeSource = useCallback(
    (sourceToRemove: ChatSource) => {
      const sourceUUID = getSourceUUID(sourceToRemove);
      if (!sourceUUID) {
        return;
      }

      const newSources = workflowAgentAddedSources.filter((s) => {
        const currentUUID = getSourceUUID(s);
        return currentUUID !== sourceUUID;
      });
      const newSourceUUIDs = newSources.map(getSourceUUID).filter(isDefined);

      setWorkflowAgentAddedSources(newSources);
      setWorkflowAgentAddedSourcesUUIDs(newSourceUUIDs);
    },
    [workflowAgentAddedSources],
  );

  const addSource = useCallback(
    (sourceToAdd: ChatSource) => {
      const uuid = getSourceUUID(sourceToAdd);
      if (!uuid) {
        return; // Skip if no UUID
      }

      const sourceUUIDs = new Set(workflowAgentAddedSources.map(getSourceUUID));
      if (sourceUUIDs.has(uuid)) {
        return; // Skip if source already exists
      }

      const newSources = [...workflowAgentAddedSources, sourceToAdd];
      const newSourceUUIDs = newSources.map(getSourceUUID).filter(isDefined);

      setWorkflowAgentAddedSources(newSources);
      setWorkflowAgentAddedSourcesUUIDs(newSourceUUIDs);
    },
    [workflowAgentAddedSources],
  );

  useEffect(() => {
    if (isEqual(workflowAgentAddedSourcesUUIDs, value)) {
      return;
    }
    onChange(workflowAgentAddedSourcesUUIDs ?? []);
  }, [workflowAgentAddedSourcesUUIDs, onChange, value]);

  return (
    <AddSourcesContent
      showTitle={false}
      enableUploads={false}
      customAddedSourcesSubtitle={i18n.t(
        "assistant_ai_tool_added_sources_subtitle",
      )}
      sources={workflowAgentAddedSources}
      sourcesContentCache={sourcesContentCache}
      addSource={addSource}
      removeSource={removeSource}
      onRequestClose={() => {}}
      logComposeEvent={() => {}}
    />
  );
};
